import { useAuth } from "@/contexts/auth/AuthContext";
import Analytics from "@/lib/analytics";
import BookmarkIcon from "./icons/Bookmark";
import GoogleIcon from "./icons/Google";
import HexagonGroup from "./icons/HexagonGroup";
import Mail from "./icons/Mail";
import XIcon from "./icons/XIcon";
import Modal from "./Modal";
import OutboundLink from "./OutboundLink";
import {
  ButtonSizes,
  ButtonTypes,
  IconTypes,
  StyledButton,
} from "@/components/StyledButton";
import { useState } from "react";
import CheckboxIcon from "./icons/Checkbox";
import CheckboxCheckedIcon from "./icons/CheckboxChecked";
import GDrive from "./icons/GDrive";
import ErrorIcon from "./icons/ErrorIcon";

const LoginModal = () => {
  const { login, showLoginPrompt, closeLoginPrompt } = useAuth();

  const [isChecked, setIsChecked] = useState(false);
  const [isValidationTriggered, setIsValidationTriggered] = useState(false);

  const handleSignInClick = async () => {
    if (isChecked) {
      await login();
      closeLoginPrompt();
    } else {
      setIsValidationTriggered(true);
    }
  };

  const handleClose = () => {
    Analytics.handleLoginPromptCloseEvent();
    closeLoginPrompt();
  };

  return (
    <Modal
      isOpen={showLoginPrompt}
      onClose={handleClose}
      contentLabel="Sign in"
      style={{ overlay: { zIndex: 10000 } }}
    >
      <div className="relative max-h-[calc(100vh-32px)] w-[480px] max-w-[calc(100vw-32px)] overflow-y-auto rounded-3xl bg-white px-10 pt-14 shadow-[0_10px_10px_rgba(0,12,16,0.10)]">
        <button
          className="absolute right-3 top-3 rounded-full p-1 hover:bg-black/5 active:bg-black/10"
          aria-label="Close Sign in Modal"
          onClick={handleClose}
        >
          <XIcon />
        </button>
        <div className="flex flex-col">
          <h2 className="text-center font-roobert text-[32px] leading-none">
            Sign in
          </h2>
          <p className="my-8 text-center text-base">
            Enhance your journey with these free<>&nbsp;</>benefits:
          </p>
          <Benefits />

          <div className="mt-10 text-left">
            <label className="flex items-center">
              <input
                type="checkbox"
                className="hidden"
                checked={isChecked}
                onChange={() => setIsChecked((prev) => !prev)}
              />
              {isChecked ? (
                <CheckboxCheckedIcon className="shrink-0" />
              ) : (
                <CheckboxIcon
                  className="shrink-0"
                  stroke={isValidationTriggered ? "#d34327" : "#121212"}
                />
              )}
              <TermsLinks />
            </label>
          </div>

          <div
            className={`flex pt-3 ${
              isValidationTriggered && !isChecked ? "visible" : "invisible"
            }`}
          >
            <WarningMessage />
          </div>

          <StyledButton
            buttonType={ButtonTypes.Secondary}
            size={ButtonSizes.Large}
            iconType={IconTypes.Left}
            className="mb-10 mt-2 w-full small:mt-6"
            onClick={handleSignInClick}
          >
            <GoogleIcon />
            Sign in with Google
          </StyledButton>
        </div>
      </div>
    </Modal>
  );
};

const Benefits = () => {
  return (
    <ul className="flex flex-col gap-2">
      <li className="flex">
        <BookmarkIcon className="mr-4 shrink-0" stroke="#212121" />
        Save your favorite resources.
      </li>
      <li className="flex">
        <HexagonGroup className="mr-4 shrink-0" fill="#212121" />
        Curate projects from your saved resources.
      </li>
      <li className="flex">
        <GDrive className="mr-4 shrink-0" fill="#212121" />
        View and copy activities in Google Drive.
      </li>
      <li className="flex">
        <Mail className="mr-4 shrink-0" />
        Stay updated on new features and content.
      </li>
    </ul>
  );
};

const WarningMessage = () => {
  return (
    <div className="ml-10 flex font-roobert text-sm font-normal text-[#d34327]">
      <ErrorIcon className="mr-1 flex shrink-0" />
      <div className="flex">
        Please confirm you are 18 or older and agree to our Privacy Policy and
        Terms of Use to proceed.
      </div>
    </div>
  );
};

const TermsLinks = () => {
  return (
    <p className="ml-4 leading-5 text-gray-text">
      {
        "By clicking this box, you confirm you are 18 or older and agree to XQ's "
      }
      <OutboundLink
        href="https://xqsuperschool.org/privacy-policy/"
        className="font-semibold underline"
      >
        Privacy<>&nbsp;</>Policy
      </OutboundLink>{" "}
      and<>&nbsp;</>
      <OutboundLink
        href="https://xqsuperschool.org/terms/"
        className="font-semibold underline"
      >
        Terms of Use
      </OutboundLink>
      .
    </p>
  );
};

export default LoginModal;
